import React, { useEffect, useMemo } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import FadeIn from '../../../../../molecules/Transitions/FadeIn'
import { useSectionEditing, useStatusTemplateContext } from '../StatusTemplateProvider'
import SectionScreen from '../../../shared/SectionScreen'
import SectionHeader from '../../../shared/SectionHeader'
import EditButton from '../../../shared/EditButton'
import PersonalSpace from '../../../shared/PersonalSpace'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SaveCancelFooter from '../../../shared/SaveCancelFooter'
import { useGetSupplementalDefinition, useUpsertSupplementalDefinitionMutation } from '../../../../../../api/groups'
import Switch from '../../../../../molecules/Switch'
import SydButton from '../../../../../commonDesign/Button'
import SydSelect from '../../../../../commonDesign/SydSelect'
import CompactIconPicker from './CompactIconPicker'
import {
  applySupplementalBadgeToDisplayConfig,
  buildSupplementalDisplayConfigPayload,
  DEFAULT_BADGE_LABEL,
  DETAIL_FIELD_FORMAT_OPTIONS,
  detailFieldsToRows,
  formatMatchValueForForm,
  parseMatchValueForSubmit,
  SUPPLEMENTAL_FIELD_MAP_KEYS,
  SUPPLEMENTAL_LAYOUT_OPTIONS,
  SUPPLEMENTAL_MATCH_OP_OPTIONS
} from './supplementalFormShared'
import {
  DEFAULT_SUPPLEMENTAL_ITEMS_PATH,
  pipelineVariableRowsToObject,
  pipelineVariablesToRows
} from './supplementalPipelineVariablesForm'

const supplementalSourceTypeOptions = [
  { label: 'Pipeline', value: 'pipeline' }
]

function SupplementalDataTab () {
  const { statusTemplate, editing, editSection, cancelEdit } = useStatusTemplateContext()
  const sectionIsEditing = useSectionEditing('supplementalPipeline')
  const { data: defData } = useGetSupplementalDefinition(statusTemplate.statusTemplateId)
  const { mutateAsync: upsertDefinition, isLoading: upserting } = useUpsertSupplementalDefinitionMutation()

  const sourceTypeSelectOptions = useMemo(() => {
    const fromApi = defData?.definition?.sourceType
    if (!fromApi || supplementalSourceTypeOptions.some((o) => o.value === fromApi)) {
      return supplementalSourceTypeOptions
    }
    return [...supplementalSourceTypeOptions, { label: fromApi, value: fromApi }]
  }, [defData?.definition?.sourceType])

  const pipelineForm = useForm({
    mode: 'onChange',
    defaultValues: {
      sourceType: 'pipeline',
      sourceKey: '',
      pipelineVariableRows: [],
      defaultMatch: {
        field: 'codeName',
        op: 'eq',
        value: '$codeName'
      },
      defaultDisplay: {
        layout: 'tasksAccordion',
        itemsPath: DEFAULT_SUPPLEMENTAL_ITEMS_PATH,
        header: '',
        summaryLabel: '',
        detailFieldRows: [],
        completedValue: '',
        fields: {
          label: '',
          status: '',
          type: ''
        },
        badge: {
          show: false,
          icon: 'list',
          style: '',
          label: ''
        }
      }
    }
  })

  const { fields: pipelineVarFields, append: appendPipelineVar, remove: removePipelineVar } = useFieldArray({
    control: pipelineForm.control,
    name: 'pipelineVariableRows'
  })

  const { fields: detailFieldRows, append: appendDetailField, remove: removeDetailField } = useFieldArray({
    control: pipelineForm.control,
    name: 'defaultDisplay.detailFieldRows'
  })

  const sourceType = useWatch({
    control: pipelineForm.control,
    name: 'sourceType',
    defaultValue: 'pipeline'
  })

  const displayLayout = useWatch({
    control: pipelineForm.control,
    name: 'defaultDisplay.layout',
    defaultValue: 'tasksAccordion'
  })

  const defaultMatchOp = useWatch({
    control: pipelineForm.control,
    name: 'defaultMatch.op',
    defaultValue: 'eq'
  })

  const isPipeline = sourceType === 'pipeline'

  useEffect(() => {
    if (sectionIsEditing) return
    const def = defData?.definition
    const dc = def?.defaultDisplayConfig
    pipelineForm.reset({
      sourceType: def?.sourceType ?? 'pipeline',
      sourceKey: def?.sourceKey ?? '',
      pipelineVariableRows: pipelineVariablesToRows(def?.pipelineVariables),
      defaultMatch: {
        field: def?.defaultMatchField ?? 'codeName',
        op: def?.defaultMatchOp ?? 'eq',
        value: def?.defaultMatchValue != null
          ? formatMatchValueForForm(def.defaultMatchValue)
          : '$codeName'
      },
      defaultDisplay: {
        layout: dc?.layout ?? 'tasksAccordion',
        itemsPath: dc?.itemsPath ?? DEFAULT_SUPPLEMENTAL_ITEMS_PATH,
        header: dc?.header ?? '',
        summaryLabel: dc?.summaryLabel ?? '',
        detailFieldRows: detailFieldsToRows(dc?.detailFields),
        completedValue: dc?.completedValue ?? '',
        fields: {
          label: dc?.fields?.label ?? '',
          status: dc?.fields?.status ?? '',
          type: dc?.fields?.type ?? ''
        },
        badge: {
          show: !!dc?.badge?.show,
          icon: dc?.badge?.icon ?? 'list',
          style: dc?.badge?.style ? JSON.stringify(dc.badge.style) : '',
          label: dc?.badge?.label ?? ''
        }
      }
    })
  }, [defData, pipelineForm, sectionIsEditing])

  const onSave = pipelineForm.handleSubmit(async (vals) => {
    const df = vals.defaultMatch?.field?.trim() ?? ''
    const dop = vals.defaultMatch?.op?.trim() ?? ''
    if (!df || !dop) {
      pipelineForm.setError('defaultMatch.field', { type: 'validate', message: 'Match field and operator are required' })
      return
    }
    const parsed = parseMatchValueForSubmit(vals.defaultMatch?.value, dop, pipelineForm, 'defaultMatch.value')
    if (parsed === undefined) return

    const built = buildSupplementalDisplayConfigPayload(vals.defaultDisplay, {
      includeItemsPath: true
    })
    if (!built) {
      pipelineForm.setError('defaultDisplay.layout', { type: 'validate', message: 'Choose a layout' })
      return
    }

    const displayWithBadge = applySupplementalBadgeToDisplayConfig(built, vals.defaultDisplay?.badge)

    const pipelineVariables = pipelineVariableRowsToObject(vals.pipelineVariableRows)
    try {
      await upsertDefinition({
        statusTemplateId: statusTemplate.statusTemplateId,
        sourceType: vals.sourceType,
        sourceKey: vals.sourceKey?.trim() ? vals.sourceKey.trim() : null,
        defaultDisplayConfig: displayWithBadge,
        pipelineVariables,
        defaultMatchField: df,
        defaultMatchOp: dop,
        defaultMatchValue: parsed
      })
      cancelEdit()
    } catch (err) {
      console.error('Failed to save supplemental definition', err)
    }
  })

  return (
    <>
      <FadeIn>
        <SectionScreen editing={editing} sectionIsEditing={sectionIsEditing}>
          <SectionHeader text='Supplemental Data' subText='Attach additional data to status report lines. Choose a source type and configure how the data is fetched, matched to lines, and displayed.'>
            <div>
              <EditButton
                policy='admin_edit_status_templates'
                editing={editing}
                onClick={() => editSection({ section: 'supplementalPipeline' })}
              />
            </div>
          </SectionHeader>
          <div className='flex flex-col gap-lg'>
            <div className='border border-neutral-300 rounded p-4 flex flex-col gap-md'>
              <h3 className='m-0 font-size-md font-weight-bold'>Data source</h3>
              <div className='flex gap-lg'>
                <Controller
                  name='sourceType'
                  control={pipelineForm.control}
                  rules={{ required: { value: true, message: 'Source type is required' } }}
                  render={({ field, fieldState }) => (
                    <SydLabel label='Source type' error={hookFormErrorAdapter(pipelineForm, fieldState)} required>
                      <SydSelect
                        {...field}
                        options={sourceTypeSelectOptions}
                        disabled={!sectionIsEditing}
                        size='sm'
                      />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='sourceKey'
                  control={pipelineForm.control}
                  rules={{ required: { value: true, message: 'Code is required' } }}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Source key'
                      description={isPipeline ? 'Data pipeline code' : 'Identifier for this source'}
                      error={hookFormErrorAdapter(pipelineForm, fieldState)}
                      required
                    >
                      <SydInput
                        {...field}
                        disabled={!sectionIsEditing}
                        size='sm'
                        placeholder={isPipeline ? 'e.g. status-report-strategic-tasks' : 'source identifier'}
                      />
                    </SydLabel>
                  )}
                />
              </div>

              {isPipeline && (
                <div>
                  <div className='flex gap-lg items-center'>
                    <div>
                      <span className='font-size-sm' style={{ fontWeight: 600 }}>Variables (optional)</span>
                      <div className='font-size-xs'>
                        Sent as <code className='font-size-xs'>variables.filters.&lt;key&gt;</code> when executing the pipeline.
                        Use <code className='font-size-xs'>$clientId</code>, <code className='font-size-xs'>$yesterday</code>, or literal values.
                      </div>
                    </div>
                    <SydButton
                      type='button'
                      size='xs'
                      icon='add'
                      disabled={!sectionIsEditing}
                      onClick={() => appendPipelineVar({ key: '', value: '' })}
                    >
                      Add
                    </SydButton>
                  </div>
                  <div className='flex flex-col gap-lg'>
                    {pipelineVarFields.map((row, index) => (
                      <div key={row.id} className='flex items-center gap-lg'>
                        <Controller
                          name={`pipelineVariableRows.${index}.key`}
                          control={pipelineForm.control}
                          render={({ field }) => (
                            <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='Key' />
                          )}
                        />
                        <Controller
                          name={`pipelineVariableRows.${index}.value`}
                          control={pipelineForm.control}
                          render={({ field }) => (
                            <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='Value' />
                          )}
                        />
                        <SydButton
                          variant='primary'
                          priority='warning'
                          type='button'
                          size='xs'
                          icon='close'
                          disabled={!sectionIsEditing}
                          onClick={() => removePipelineVar(index)}
                        >
                          Remove
                        </SydButton>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Default match ── */}
            <div className='border border-neutral-300 rounded p-4 flex flex-col gap-md'>
              <h3 className='m-0 font-size-md font-weight-bold'>Default row matching</h3>
              <div className='font-size-xs'>
                Determines which rows from the data source map to each status report line.
                Lines with a per-item mapping override these defaults.
              </div>
              <div className='flex flex-row gap-lg'>
                <Controller
                  name='defaultMatch.field'
                  control={pipelineForm.control}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Field on data row'
                      error={hookFormErrorAdapter(pipelineForm, fieldState)}
                      required
                    >
                      <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='codeName' />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='defaultMatch.op'
                  control={pipelineForm.control}
                  render={({ field }) => (
                    <SydLabel label='Operator' required>
                      <SydSelect
                        {...field}
                        options={SUPPLEMENTAL_MATCH_OP_OPTIONS}
                        disabled={!sectionIsEditing}
                        size='sm'
                      />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='defaultMatch.value'
                  control={pipelineForm.control}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Value'
                      description={
                        defaultMatchOp === 'in' || defaultMatchOp === 'nin'
                          ? 'JSON array or comma-separated list'
                          : 'Literal or $codeName / $statusReportItemId'
                      }
                      error={hookFormErrorAdapter(pipelineForm, fieldState)}
                      required
                    >
                      {defaultMatchOp === 'in' || defaultMatchOp === 'nin' ? (
                        <textarea
                          className='font-size-sm'
                          rows={3}
                          style={{ width: '100%', padding: 'var(--dim-2)', borderRadius: 'var(--border-radius-sm)', border: 'var(--border-neutral-thin)' }}
                          placeholder='["a","b"]'
                          disabled={!sectionIsEditing}
                          {...field}
                        />
                      ) : (
                        <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='$codeName' />
                      )}
                    </SydLabel>
                  )}
                />
              </div>
            </div>

            {/* ── Default display ── */}
            <div className='border border-neutral-300 rounded p-4 flex flex-col gap-md'>
              <h3 className='m-0 font-size-md font-weight-bold'>Default display</h3>
              <div className='font-size-xs'>
                Controls how matched data renders for lines without a per-item override.
              </div>
              <div className='flex gap-lg'>
                <Controller
                  name='defaultDisplay.layout'
                  control={pipelineForm.control}
                  render={({ field, fieldState }) => (
                    <SydLabel label='Layout' error={hookFormErrorAdapter(pipelineForm, fieldState)}>
                      <SydSelect
                        {...field}
                        options={[{ label: '—', value: '' }, ...SUPPLEMENTAL_LAYOUT_OPTIONS]}
                        disabled={!sectionIsEditing}
                        size='sm'
                      />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='defaultDisplay.itemsPath'
                  control={pipelineForm.control}
                  render={({ field }) => (
                    <SydLabel label='Items path' description={`Dot path to array (default: ${DEFAULT_SUPPLEMENTAL_ITEMS_PATH})`}>
                      <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder={DEFAULT_SUPPLEMENTAL_ITEMS_PATH} />
                    </SydLabel>
                  )}
                />
                {displayLayout === 'tasksAccordion' && (
                  <Controller
                    name='defaultDisplay.summaryLabel'
                    control={pipelineForm.control}
                    render={({ field }) => (
                      <SydLabel label='Accordion summary text' description='Text after the count (default: tasks complete)'>
                        <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='tasks complete' />
                      </SydLabel>
                    )}
                  />
                )}
              </div>
            </div>

            {/* ── Field mapping ── */}
            <div className='border border-neutral-300 rounded p-4 flex flex-col gap-md'>
              <h3 className='m-0 font-size-md font-weight-bold'>Field mapping</h3>
              <div className='font-size-xs'>
                Map which fields from each data row are used for the label, status, and type badge.
                Leave blank to use the built-in defaults.
              </div>
              <div className='flex gap-lg'>
                {SUPPLEMENTAL_FIELD_MAP_KEYS.map(({ key, label, placeholder, defaultField }) => (
                  <Controller
                    key={key}
                    name={`defaultDisplay.fields.${key}`}
                    control={pipelineForm.control}
                    render={({ field }) => (
                      <SydLabel label={label} description={`Default: ${defaultField}`}>
                        <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder={placeholder} />
                      </SydLabel>
                    )}
                  />
                ))}
              </div>
              <Controller
                name='defaultDisplay.completedValue'
                control={pipelineForm.control}
                render={({ field }) => (
                  <SydLabel label='Completed status value' description='Status field value that triggers "done" styling (strikethrough + green). Default: Completed'>
                    <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='Completed' />
                  </SydLabel>
                )}
              />
            </div>

            {/* ── Detail panel ── */}
            <div className='border border-neutral-300 rounded p-4 flex flex-col gap-md'>
              <h3 className='m-0 font-size-md font-weight-bold'>Detail panel</h3>
              <div className='font-size-xs'>
                Configure what appears in the detail side panel when a report line is selected.
              </div>
              <Controller
                name='defaultDisplay.header'
                control={pipelineForm.control}
                render={({ field }) => (
                  <SydLabel label='Section header'>
                    <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='e.g. Tasks' />
                  </SydLabel>
                )}
              />
              <div>
                <div className='flex gap-lg items-center'>
                  <div>
                    <span className='font-size-sm' style={{ fontWeight: 600 }}>Expanded detail fields</span>
                    <div className='font-size-xs'>
                      Additional fields shown when a row is expanded. Map a data key, display label, and optional format.
                    </div>
                  </div>
                  <SydButton
                    type='button'
                    size='xs'
                    icon='add'
                    disabled={!sectionIsEditing}
                    onClick={() => appendDetailField({ field: '', label: '', format: '' })}
                  >
                    Add
                  </SydButton>
                </div>
                <div className='flex flex-col gap-sm mt-2'>
                  {detailFieldRows.map((row, index) => (
                    <div key={row.id} className='flex items-center gap-md'>
                      <Controller
                        name={`defaultDisplay.detailFieldRows.${index}.field`}
                        control={pipelineForm.control}
                        render={({ field }) => (
                          <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='Data key (e.g. Subject)' />
                        )}
                      />
                      <Controller
                        name={`defaultDisplay.detailFieldRows.${index}.label`}
                        control={pipelineForm.control}
                        render={({ field }) => (
                          <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='Display label' />
                        )}
                      />
                      <Controller
                        name={`defaultDisplay.detailFieldRows.${index}.format`}
                        control={pipelineForm.control}
                        render={({ field }) => (
                          <SydSelect {...field} options={DETAIL_FIELD_FORMAT_OPTIONS} disabled={!sectionIsEditing} size='sm' />
                        )}
                      />
                      <SydButton
                        variant='primary'
                        priority='warning'
                        type='button'
                        size='xs'
                        icon='close'
                        disabled={!sectionIsEditing}
                        onClick={() => removeDetailField(index)}
                      >
                        Remove
                      </SydButton>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Badge ── */}
            <div className='border border-neutral-300 rounded p-4 flex flex-col gap-md'>
              <h3 className='m-0 font-size-md font-weight-bold'>Row count badge</h3>
              <div className='font-size-xs'>
                Show an item count next to each report line in the board / scorecard layout.
              </div>
              <div className='flex items-center gap-lg'>
                <Controller
                  name='defaultDisplay.badge.show'
                  control={pipelineForm.control}
                  render={({ field }) => (
                    <SydLabel label='Show count'>
                      <Switch {...field} checked={!!field.value} disabled={!sectionIsEditing} />
                    </SydLabel>
                  )}
                />
              </div>
              <div className='flex gap-lg'>
                <Controller
                  name='defaultDisplay.badge.label'
                  control={pipelineForm.control}
                  render={({ field }) => (
                    <SydLabel label='Count label' description={`Text after the number (default: "${DEFAULT_BADGE_LABEL}")`}>
                      <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder={DEFAULT_BADGE_LABEL} />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='defaultDisplay.badge.style'
                  control={pipelineForm.control}
                  render={({ field }) => (
                    <SydLabel label='Inline styles (optional)' description='JSON style object, e.g. { "color": "var(--color-primary-500)" }'>
                      <SydInput {...field} disabled={!sectionIsEditing} size='sm' placeholder='{ "color": "var(--color-primary-500)" }' />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='defaultDisplay.badge.icon'
                  control={pipelineForm.control}
                  render={({ field }) => (
                    <SydLabel label='Icon'>
                      <CompactIconPicker
                        value={field.value}
                        onSelect={(name) => field.onChange(name)}
                        disabled={!sectionIsEditing}
                      />
                    </SydLabel>
                  )}
                />
              </div>
            </div>
          </div>
          <PersonalSpace />
        </SectionScreen>
      </FadeIn>
      {sectionIsEditing ? (
        <SaveCancelFooter
          saveText='Save'
          onCancel={cancelEdit}
          onSave={onSave}
          processing={upserting}
          disabled={!pipelineForm.formState.isValid}
        />
      ) : null}
    </>
  )
}

SupplementalDataTab.propTypes = {}

export default SupplementalDataTab
